/**
 * TypeScript wrapper for the cloud-shimmer AudioWorklet processor.
 * Handles worklet loading, parameter management, and Tone.js integration.
 *
 * Signal chain: Input -> Shimmer Reverb -> Cloud Reverb -> Output
 * Both reverbs are independent FDNs with their own parameters.
 */

import { getNativeAudioContext } from "./get-native-context";

export interface CloudReverbParams {
  // Cloud reverb
  cloudDecay: number;
  cloudPreDelay: number;
  cloudDamping: number;
  cloudModDepth: number;
  cloudModRate: number;
  cloudDiffusion: number;
  cloudMix: number;

  // Shimmer reverb
  shimmerDecay: number;
  shimmerPreDelay: number;
  shimmerDamping: number;
  shimmerModDepth: number;
  shimmerModRate: number;
  shimmerDiffusion: number;
  shimmerMix: number;
  shimmerAmount: number;
  shimmerPitch1: number;
  shimmerPitch2: number;
  shimmerBalance: number;
}

const DEFAULT_PARAMS: CloudReverbParams = {
  cloudDecay: 8,
  cloudPreDelay: 0.05,
  cloudDamping: 0.3,
  cloudModDepth: 0.5,
  cloudModRate: 0.7,
  cloudDiffusion: 0.75,
  cloudMix: 0.5,

  shimmerDecay: 6,
  shimmerPreDelay: 0.01,
  shimmerDamping: 0.25,
  shimmerModDepth: 0.4,
  shimmerModRate: 0.5,
  shimmerDiffusion: 0.7,
  shimmerMix: 0.5,
  shimmerAmount: 0.4,
  shimmerPitch1: 12,
  shimmerPitch2: 24,
  shimmerBalance: 0.3,
};

export class CloudReverbNode {
  private workletNode: AudioWorkletNode | null = null;
  private _params: CloudReverbParams;
  private _loaded = false;

  input: GainNode | null = null;
  output: GainNode | null = null;

  constructor(params?: Partial<CloudReverbParams>) {
    this._params = { ...DEFAULT_PARAMS, ...params };
  }

  async load(): Promise<void> {
    if (this._loaded) return;

    const ctx = await getNativeAudioContext();

    await ctx.audioWorklet.addModule("/worklets/cloud-shimmer-processor.js");

    this.workletNode = new AudioWorkletNode(ctx, "cloud-shimmer-processor", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });

    this.input = ctx.createGain();
    this.output = ctx.createGain();

    this.input.connect(this.workletNode);
    this.workletNode.connect(this.output);

    this.workletNode.port.postMessage(this._params);

    this._loaded = true;
  }

  get loaded(): boolean {
    return this._loaded;
  }

  get params(): Readonly<CloudReverbParams> {
    return this._params;
  }

  setParam<K extends keyof CloudReverbParams>(
    key: K,
    value: CloudReverbParams[K],
  ): void {
    this._params[key] = value;
    this.workletNode?.port.postMessage({ [key]: value });
  }

  setParams(params: Partial<CloudReverbParams>): void {
    Object.assign(this._params, params);
    this.workletNode?.port.postMessage(params);
  }

  connectFrom(source: { connect: (dest: AudioNode) => void }): void {
    if (!this.input) return;
    source.connect(this.input);
  }

  connectTo(dest: AudioNode): void {
    if (!this.output) return;
    this.output.connect(dest);
  }

  dispose(): void {
    this.workletNode?.disconnect();
    this.input?.disconnect();
    this.output?.disconnect();
    this.workletNode = null;
    this.input = null;
    this.output = null;
    this._loaded = false;
  }
}
